import { type SignInOutput, useConfirmSignIn } from 'korint-sdk/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { type Challenge, type MfaType, resolveSignInChallenge } from './resolveSignInChallenge';
import { Stack } from '@folio/components/ui/flex';

export function MfaSelectionForm({
  email,
  allowedMfaTypes,
  onChallenge,
  onSuccess,
}: {
  email: string;
  allowedMfaTypes: MfaType[];
  onChallenge: (challenge: Challenge) => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [pendingType, setPendingType] = useState<MfaType | null>(null);

  const { confirmSignIn } = useConfirmSignIn({
    onSuccess: (result: SignInOutput | undefined) => {
      setPendingType(null);
      if (result?.isSignedIn) {
        onSuccess();
        return;
      }
      const next = result ? resolveSignInChallenge(result.nextStep, email) : null;
      if (next) {
        onChallenge(next);
        return;
      }
      setError(t('authMfaSelection.errorGeneric'));
    },
    onError: err => {
      setPendingType(null);
      setError(err instanceof Error ? err.message : t('authMfaSelection.errorGeneric'));
    },
  });

  const choose = async (type: MfaType) => {
    setError(null);
    setPendingType(type);
    await confirmSignIn({ challengeResponse: type });
  };

  return (
    <Stack gap={3}>
      {error && <Alert variant="destructive">{error}</Alert>}
      {allowedMfaTypes.includes('SMS') && (
        <Button
          className="w-full"
          variant="outline"
          loading={pendingType === 'SMS'}
          disabled={pendingType !== null && pendingType !== 'SMS'}
          onClick={() => choose('SMS')}
        >
          {t('authMfaSelection.useSms')}
        </Button>
      )}
      {allowedMfaTypes.includes('TOTP') && (
        <Button
          className="w-full"
          variant="outline"
          loading={pendingType === 'TOTP'}
          disabled={pendingType !== null && pendingType !== 'TOTP'}
          onClick={() => choose('TOTP')}
        >
          {t('authMfaSelection.useTotp')}
        </Button>
      )}
    </Stack>
  );
}
